import React, { useState, useContext } from "react"; // <-- Added useContext
import { useNavigate } from "react-router-dom";
import {
  FiThumbsUp,
  FiThumbsDown,
  FiShare2,
  FiMessageSquare,
  FiFlag,
  FiBookmark,
  FiMoreHorizontal,
} from "react-icons/fi";
import Avatar from "./components/Avatar";
import FeedVideo from "./components/FeedVideo";
import MentionText from "./components/MentionText";
import api from "./utils/api";
import { createSlugUrl } from "./utils/urlHelper";
import toast from "react-hot-toast";

// Connect component to global authorization track
import { AuthContext } from "./contexts/AuthContext";

const MAX_BODY_CHARS = 260;

export default function FeedItemCard({ item, onRequireLogin, priority = false }) { // <-- Destructured onRequireLogin prop
  const navigate = useNavigate();

  // 1. DATA EXTRACTION
  const title = item.title || item.recipe_title || item.post_title || "";
  const body = item.content || item.body || item.description || item.caption || "";
  const uuid = item.post_uuid || item.item_uuid || item.id;
  const username = item.username || item.userName || item.author_username || item.user?.username || item.user?.userName || "user";
  const avatarUrl = item.avatar_url || item.user?.avatar_url || item.author_avatar_url || null;
  const avatarUUID = item.user_uuid || item.user?.user_uuid || item.author_uuid || item.author_user_uuid || username;
  const mediaUrl = item.feed_url || item.mime_url || item.image_url || item.photo_url || item.media_url || item.video_url || "";
  const remixSourceUsername = item.remix_source_username || item.source_username || null;
  
  // Detect Media Type
  const isVideo = item.mime_type?.startsWith("video") || mediaUrl.match(/\.(mp4|mov|webm)$/i);
  const isImage = item.mime_type?.startsWith("image") || mediaUrl.match(/\.(jpg|jpeg|png|webp|gif)$/i);
  const hasMedia = Boolean(mediaUrl && (isVideo || isImage));

  // 2. STATE
  const [upvotes, setUpvotes] = useState(item.upvotes || 0);
  const [downvotes, setDownvotes] = useState(item.downvotes || 0);
  const [userVote, setUserVote] = useState(item.user_vote || null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [saved, setSaved] = useState(!!item.saved);

  // Use application context for robust session checking
  const { user, accessToken } = useContext(AuthContext);
  const isLoggedIn = !!accessToken || !!user;
  
  const postUrl = createSlugUrl(username, "discussion", title, uuid);

  // 3. HANDLERS
  const handleVote = async (e, type) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Intercept with the parent's login modal trigger
    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }
    
    try {
      const res = await api.post("/votes", { post_uuid: uuid, vote: type });
      setUpvotes(res.data.upvotes ?? 0);
      setDownvotes(res.data.downvotes ?? 0);
      setUserVote(res.data.user_vote);
    } catch (err) { console.error("Vote error:", err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Intercept with the parent's login modal trigger
    if (!isLoggedIn) { 
      onRequireLogin();
      setMenuOpen(false); 
      return; 
    }
    
    try {
      if (!saved) {
        await api.post(`/posts/${uuid}/save`, {});
        setSaved(true);
        toast.success("Post saved! 💾");
      } else {
        await api.delete(`/posts/${uuid}/save`);
        setSaved(false);
        toast.success("Removed from saved items.");
      }
    } catch (err) { console.error("Save error:", err); }
    setMenuOpen(false);
  };

  const handleFlagPost = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Intercept with the parent's login modal trigger
    if (!isLoggedIn) { 
      onRequireLogin();
      setMenuOpen(false); 
      return; 
    }
    
    toast.success("Post flagged for review.");
    setMenuOpen(false);
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation(); 

    const absoluteUrl = `${window.location.origin}${postUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: `Check out this discussion by @${username}!`,
          url: absoluteUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("Share error:", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(absoluteUrl);
        toast.success("Link copied to clipboard! 📋");
      } catch (err) {
        console.error("Clipboard copy error:", err);
        toast.error("Failed to copy link");
      }
    }
  };

  const handleNavigate = () => navigate(postUrl, { state: { post: item } });

  // 4. RENDER
  if (item.is_ad || item.type === "ad") {
    return (
      <div onClick={() => window.open(item.target_url, "_blank")} className="bg-yellow-50 rounded-2xl p-4 mb-6 cursor-pointer border border-yellow-100">
        <span className="text-[10px] font-bold uppercase tracking-wider text-yellow-600">Sponsored</span>
        <h4 className="font-bold text-gray-900 mt-1 mb-2">{title}</h4>
        {hasMedia && <img src={mediaUrl} className="w-full h-48 object-cover rounded-xl" alt="ad" />}
      </div>
    );
  }

  return (
    <div onClick={handleNavigate} className="bg-white border border-gray-100 rounded-3xl p-5 mb-8 hover:shadow-xl hover:shadow-gray-100 transition-all cursor-pointer group">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 relative">
        <div className="flex items-center gap-3">
          <Avatar src={avatarUrl} userUUID={avatarUUID} className="w-10 h-10 border-2 border-white shadow-sm" />
          <div>
            <p className="text-sm font-bold text-gray-900">@{username}</p>
            <p className="text-[11px] text-gray-400 uppercase tracking-tight">Discussion</p>
          </div>
        </div>

        {/* Dropdown Options Anchor */}
        {isLoggedIn && (
        <div className="relative">
          <button 
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }} 
            className="text-gray-400 hover:text-gray-600 p-2 focus:outline-none"
          >
            <FiMoreHorizontal size={20} />
          </button>

          {/* Context Dropdown Component Popup Panel */}
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-20" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(false); }} />
              <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-100 rounded-xl shadow-xl py-1.5 z-30">
                <button 
                  onClick={handleSave} 
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition"
                >
                  {saved ? (
                    <>
                      <FiBookmark size={16} className="fill-slate-900 text-slate-900" />
                      <span className="font-medium text-slate-900">Saved</span>
                    </>
                  ) : (
                    <>
                      <FiBookmark size={16} />
                      <span>Save Post</span>
                    </>
                  )}
                </button>
                <button 
                  onClick={handleFlagPost} 
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition"
                >
                  <FiFlag size={16} />
                  <span>Flag Post</span>
                </button>
              </div>
            </>
          )}
        </div>
        )}
      </div>

      {/* Content */}
      {remixSourceUsername && (
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-orange-600">
          Remixed from @{remixSourceUsername}
        </p>
      )}

      <h3 className="text-xl font-bold text-gray-900 mb-2 leading-tight">{title}</h3>
      
      {body && (
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          <MentionText text={body} />
        </p>
      )}

      {/* Unified Media Container */}
      {hasMedia && (
        <div className="flex justify-center bg-gray-950 rounded-2xl overflow-hidden mb-4 shadow-sm border border-gray-100">
          {isVideo ? (
            <FeedVideo src={mediaUrl} poster={item.thumbnail_url} className="h-auto max-h-[500px] w-auto object-contain" />
          ) : (
            <img 
              src={mediaUrl} 
              alt={title}
              loading={priority ? "eager" : "lazy"}
              fetchPriority={priority ? "high" : "auto"}
              decoding="async"
              className="h-auto max-h-[500px] w-auto object-contain" 
            />
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-2 relative">
        {/* LEFT: Votes */}
        <div className="flex items-center gap-1 bg-gray-50 rounded-full p-1 z-10">
          <button 
            onClick={(e) => handleVote(e, "up")} 
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition ${userVote === "up" ? "bg-orange-500 text-white shadow-md shadow-orange-200" : "hover:bg-gray-200 text-gray-600"}`}
          >
            <FiThumbsUp size={16} /> 
            {upvotes > 0 && <span className="text-xs font-bold">{upvotes}</span>}
          </button>
          <button onClick={(e) => handleVote(e, "down")} className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition ${userVote === "down" ? "bg-gray-800 text-white" : "hover:bg-gray-200 text-gray-600"}`}>
            <FiThumbsDown size={16} />
          </button>
        </div>

        {/* MIDDLE: Comments */}
        <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-1.5 text-gray-400 hover:text-orange-500 transition z-10">
          <FiMessageSquare size={18} />
          <span className="text-xs font-bold">{item.comments_count || 0}</span>
        </div>

        {/* RIGHT: Share */}
        <button 
          onClick={handleShare} 
          className="flex items-center text-gray-400 hover:text-orange-500 transition z-10 p-2 cursor-pointer focus:outline-none"
          title="Share Post"
        >
          <FiShare2 size={18} />
        </button>
      </div>
    </div>
  );
}